import { useForm } from 'antd/lib/form/Form';
import { IAPIAppConfigItem } from '../../../model/api-modules/app-config';
import { ModalWrap } from '../../component/modal-wrap';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import { useEffect, useState } from 'react';
import { IJsonEditorError } from '../../../components/json-editor/model';
import {
  FORM_LAYOUT,
  JSONConfigFieldValidator,
  LINE_FORM_LAYOUT,
  getEnvOptions,
  getRegionOptions,
  getAppNameOptions,
} from './lib/form';
import { addAdminAppConfig, putAdminAppConfig } from '../../../api';
import { IAppConfigUpdateType } from '../../../model';
import { ReactJsonEditor } from '../../../components/react-json-editor';
import { messageError, messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';

interface IProps {
  readonly onClose: (status?: boolean) => void;
  readonly row: IAPIAppConfigItem | null;
  readonly type: IAppConfigUpdateType;
}

export const ConfigModal = (props: IProps) => {
  const [form] = useForm<IAPIAppConfigItem>();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [init, setInit] = useState<boolean>(false);
  const [customError, setCustomError] = useState<string>('');

  const showSuccessMessage = () => {
    messageSuccess(
      props.type === IAppConfigUpdateType.Create
        ? getFormatMessage('create_success')
        : getFormatMessage('edit_success')
    );
  };

  const getModalTitle = () => {
    if (props.type === IAppConfigUpdateType.Create) {
      return getFormatMessage('create_service');
    }
    return getFormatMessage('edit_service');
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const jsonValidator = () => {
    setCustomError('');
    const val = form.getFieldValue('JsonConfig');
    let obj = null;
    try {
      obj = JSON.parse(val);
    } catch (error) {}
    if (!obj) return Promise.reject();
    const error = JSONConfigFieldValidator(obj);
    if (error) {
      return Promise.reject(error);
    }
    return Promise.resolve();
  };

  const onError = (error: IJsonEditorError | false) => {
    setError(!!error);
  };

  const onJsonChange = (json?: Object) => {
    form.setFieldValue('JsonConfig', JSON.stringify(json) || '');
  };

  const onSubmit = async () => {
    if (error) {
      console.error(getFormatMessage('json_config_error'));
      return;
    }

    // await jsonValidator().catch((e) => {
    //   setCustomError(e);
    // });

    setCustomError('');
    setLoading(true);

    let values = null;
    try {
      values = await form.validateFields();
    } catch (error) {
      console.error(getFormatMessage('validate_error'), error);
    }

    if (values) {
      let code = -1;
      const params = Object.assign({}, values || {}, {
        JsonConfig: JSON.parse(values.JsonConfig || '{}'),
      });
      if (props.type === IAppConfigUpdateType.Edit && props.row) {
        code = await putAdminAppConfig(props.row.id, {
          App: params.App,
          Env: params.Env,
          JsonConfig: params.JsonConfig,
          Region: params.Region,
        });
      } else if (props.type === IAppConfigUpdateType.Create) {
        code = await addAdminAppConfig(params);
      }

      if (code === 0) {
        showSuccessMessage();
        props.onClose(true);
      } else {
        messageError(getFormatMessage('error_update'));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (props.type === IAppConfigUpdateType.Edit) {
      form.setFieldsValue({ ...props.row });
    }
    setInit(true);
  }, [props.type]);

  const renderCustomError = () => {
    if (!customError) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <div
          id="JsonConfig_help"
          className="ant-form-item-explain ant-form-item-explain-connected"
          role="alert"
        >
          <div className="ant-form-item-explain-error">{customError}</div>
        </div>
      </div>
    );
  };

  const renderFormItems = () => {
    const disabled = props.type === IAppConfigUpdateType.Edit;
    return (
      <>
        <Row>
          <Col span={12}>
            <Form.Item
              {...FORM_LAYOUT}
              name="App"
              label={getFormatMessage('app')}
              rules={[{ required: true }]}
            >
              <Select options={getAppNameOptions()} placeholder="" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...FORM_LAYOUT}
              name="Region"
              label={getFormatMessage('region')}
              rules={[{ required: true }]}
            >
              <Select options={getRegionOptions()} placeholder="" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item
              {...FORM_LAYOUT}
              name="Domain"
              label={getFormatMessage('domain')}
              rules={[{ required: true }]}
            >
              <Input
                disabled={disabled}
                placeholder={getFormatMessage('domain_host_name')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...FORM_LAYOUT}
              name="Env"
              label={getFormatMessage('env')}
              rules={[{ required: true }]}
            >
              <Select options={getEnvOptions()} placeholder="" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          {...LINE_FORM_LAYOUT}
          // rules={[{ required: true }, { validator: jsonValidator }]}
          labelAlign="right"
          name="JsonConfig"
          label={getFormatMessage('config')}
        >
          <ReactJsonEditor
            onError={onError}
            onChange={onJsonChange}
            id="app-config-json-editor"
            height={550}
            json={JSON.parse(form.getFieldValue('JsonConfig') || '{}')}
          />
          {renderCustomError()}
        </Form.Item>
      </>
    );
  };

  if (!init) return null;
  return (
    <ModalWrap
      width="1200px"
      onOk={() => Promise.resolve(true)}
      onClose={() => props.onClose()}
      title={<p className="fail-title">{getModalTitle()}</p>}
    >
      <Form form={form}>
        {renderFormItems()}
        <Row style={{ marginLeft: '5px' }}>
          <Col
            span={LINE_FORM_LAYOUT.wrapperCol.span}
            offset={LINE_FORM_LAYOUT.labelCol.span}
          >
            <Button
              loading={loading}
              className="gc-btn gc-btn-primary btn-admin"
              onClick={onSubmit}
            >
              {getFormatMessage('submit')}
            </Button>
          </Col>
        </Row>
      </Form>
    </ModalWrap>
  );
};
